import React from "react";
import { useTranslations } from "next-intl";
import Head from "next/head";
import { WEBPAGE_TITLE } from "@utils/Constants";
import Page from "@components/Layout/page";
import { Leaf } from "@components/Leaf/Leaf";
import { useInView } from "@hooks/useInView";
import styles from "./SounargreiningPage.module.scss";

export default function SounargreiningPage() {
    const t = useTranslations("SounargreiningPage");

    const [heroRef, heroInView] = useInView();
    const [measureRef, measureInView] = useInView();
    const [resultsRef, resultsInView] = useInView();
    const [ctaRef, ctaInView] = useInView();

    return (
        <Page fluid bodyClassName={styles.pageBody}>
            <Head>
                <title key="site_title">
                    {"Sóunargreining - " + WEBPAGE_TITLE}
                </title>
                <meta
                    name="keywords"
                    content="Sóunargreining, úrgangur, orka, sóun"
                />
                <meta
                    property="og:title"
                    content={`${WEBPAGE_TITLE} - Sóunargreining`}
                    key="og:title"
                />
                <meta
                    name="og:description"
                    content={t("hero_desc")}
                    key="og:description"
                />
                <meta name="description" content={t("hero_desc")} />
            </Head>

            <div className={styles.wrapper}>
                <Leaf className={styles.leafBg1} plain />
                <Leaf className={styles.leafBg2} plain />
                <Leaf className={styles.leafBg3} plain />

                <section
                    ref={heroRef}
                    className={`${styles.hero} ${styles.revealTarget} ${heroInView ? styles.revealed : ""}`}
                >
                    <div className={styles.heroContent}>
                        <span className={styles.badge}>{t("badge")}</span>
                        <h1 className={styles.heroTitle}>{t("hero_title")}</h1>
                        <p className={styles.heroSubtitle}>
                            {t("hero_subtitle")}
                        </p>
                        <p className={styles.heroDesc}>{t("hero_desc")}</p>
                        <a className={styles.ctaBtn} href="/hafa-samband">
                            {t("contact_us")}
                        </a>
                    </div>
                </section>

                <div className={styles.showcase}>
                    <section
                        ref={measureRef}
                        className={`${styles.showcaseRow} ${styles.revealTarget} ${measureInView ? styles.revealed : ""}`}
                    >
                        <div className={styles.showcaseText}>
                            <span className={styles.showcaseLabel}>
                                {t("showcase_measure_label")}
                            </span>
                            <h2 className={styles.showcaseTitle}>
                                {t("showcase_measure_title")}
                            </h2>
                            <p className={styles.showcaseDesc}>
                                {t("showcase_measure_desc")}
                            </p>
                        </div>
                        <div className={styles.showcaseVisual}>
                            <div className={styles.statGrid}>
                                <div className={styles.statCard}>
                                    <div className={styles.statIcon}>
                                        <svg
                                            width="24"
                                            height="24"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        >
                                            <polyline points="3 6 5 6 21 6" />
                                            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                                        </svg>
                                    </div>
                                    <span className={styles.statLabel}>
                                        {t("stat_waste")}
                                    </span>
                                    <span className={styles.statMeta}>
                                        {t("stat_waste_desc")}
                                    </span>
                                </div>
                                <div className={styles.statCard}>
                                    <div className={styles.statIcon}>
                                        <svg
                                            width="24"
                                            height="24"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        >
                                            <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
                                        </svg>
                                    </div>
                                    <span className={styles.statLabel}>
                                        {t("stat_energy")}
                                    </span>
                                    <span className={styles.statMeta}>
                                        {t("stat_energy_desc")}
                                    </span>
                                </div>
                                <div className={styles.statCard}>
                                    <div className={styles.statIcon}>
                                        <svg
                                            width="24"
                                            height="24"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        >
                                            <path d="M12 2.69l5.66 5.66a8 8 0 1 1-11.31 0z" />
                                        </svg>
                                    </div>
                                    <span className={styles.statLabel}>
                                        {t("stat_water")}
                                    </span>
                                    <span className={styles.statMeta}>
                                        {t("stat_water_desc")}
                                    </span>
                                </div>
                                <div
                                    className={`${styles.statCard} ${styles.statCardAccent}`}
                                >
                                    <div className={styles.statIcon}>
                                        <svg
                                            width="24"
                                            height="24"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        >
                                            <path d="M18 8h1a4 4 0 0 1 0 8h-1" />
                                            <path d="M2 8h16v9a4 4 0 0 1-4 4H6a4 4 0 0 1-4-4V8z" />
                                            <line x1="6" y1="1" x2="6" y2="4" />
                                            <line
                                                x1="10"
                                                y1="1"
                                                x2="10"
                                                y2="4"
                                            />
                                            <line
                                                x1="14"
                                                y1="1"
                                                x2="14"
                                                y2="4"
                                            />
                                        </svg>
                                    </div>
                                    <span className={styles.statLabel}>
                                        {t("stat_food")}
                                    </span>
                                    <span className={styles.statMeta}>
                                        {t("stat_food_desc")}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </section>

                    <section
                        ref={resultsRef}
                        className={`${styles.showcaseRow} ${styles.reversed} ${styles.revealTarget} ${resultsInView ? styles.revealed : ""}`}
                    >
                        <div className={styles.showcaseText}>
                            <span className={styles.showcaseLabel}>
                                {t("showcase_results_label")}
                            </span>
                            <h2 className={styles.showcaseTitle}>
                                {t("showcase_results_title")}
                            </h2>
                            <p className={styles.showcaseDesc}>
                                {t("showcase_results_desc")}
                            </p>
                        </div>
                        <div className={styles.showcaseVisual}>
                            <div className={styles.resultStack}>
                                <div className={styles.resultCard}>
                                    <div className={styles.resultIcon}>
                                        <svg
                                            width="24"
                                            height="24"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        >
                                            <circle cx="11" cy="11" r="8" />
                                            <line
                                                x1="21"
                                                y1="21"
                                                x2="16.65"
                                                y2="16.65"
                                            />
                                        </svg>
                                    </div>
                                    <div className={styles.resultContent}>
                                        <span className={styles.resultLabel}>
                                            {t("result_insight")}
                                        </span>
                                        <span className={styles.resultMeta}>
                                            {t("result_insight_desc")}
                                        </span>
                                    </div>
                                </div>
                                <div className={styles.resultCard}>
                                    <div className={styles.resultIcon}>
                                        <svg
                                            width="24"
                                            height="24"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        >
                                            <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
                                        </svg>
                                    </div>
                                    <div className={styles.resultContent}>
                                        <span className={styles.resultLabel}>
                                            {t("result_tracking")}
                                        </span>
                                        <span className={styles.resultMeta}>
                                            {t("result_tracking_desc")}
                                        </span>
                                    </div>
                                </div>
                                <div className={styles.resultCard}>
                                    <div className={styles.resultIcon}>
                                        <svg
                                            width="24"
                                            height="24"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        >
                                            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                                            <circle cx="9" cy="7" r="4" />
                                            <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                                            <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                                        </svg>
                                    </div>
                                    <div className={styles.resultContent}>
                                        <span className={styles.resultLabel}>
                                            {t("result_culture")}
                                        </span>
                                        <span className={styles.resultMeta}>
                                            {t("result_culture_desc")}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>
                </div>

                <section
                    ref={ctaRef}
                    className={`${styles.bottomCta} ${styles.revealTarget} ${ctaInView ? styles.revealed : ""}`}
                >
                    <div className={styles.bottomCtaContent}>
                        <h2 className={styles.bottomCtaTitle}>
                            {t("bottom_title")}
                        </h2>
                        <p className={styles.bottomCtaDesc}>
                            {t("bottom_desc")}
                        </p>
                        <a className={styles.ctaBtnLight} href="/hafa-samband">
                            {t("contact_us")}
                        </a>
                    </div>
                </section>
            </div>
        </Page>
    );
}
